/**
 * useShiftGpsStopDetector — Phase 2
 * Monitors GPS position during an active shift.
 * Detects immobility ≥ 5 minutes and triggers a mandatory classification popup.
 * Uses the existing watchPosition from useGpsTracking hook data.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import config from 'config';

const API = config.API_URL;
const STOP_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes
const MOVEMENT_THRESHOLD_M = 50; // 50 meters = considered "moved"
const CHECK_INTERVAL_MS = 30000; // Check every 30s

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token') || localStorage.getItem('token')}`
});

/**
 * Haversine distance in meters between two lat/lng pairs
 */
function distanceM(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function useShiftGpsStopDetector(shiftActive = false) {
  const [pendingStop, setPendingStop] = useState(null); // { stop_id } when popup should show
  const [detecting, setDetecting] = useState(false);

  const anchorRef = useRef(null); // { lat, lng, since: timestamp }
  const watchIdRef = useRef(null);

  const resetAnchor = useCallback((lat, lng) => {
    anchorRef.current = { lat, lng, since: Date.now() };
  }, []);

  const reportStop = useCallback(async () => {
    try {
      const { data } = await axios.post(
        `${API}/api/chauffeur/shifts/current/gps-stop`,
        {},
        { headers: getHeaders() }
      );
      setPendingStop({ stop_id: data.stop_id });
    } catch (err) {
      console.warn('[StopDetector] Report failed:', err.message);
    }
  }, []);

  const classifyStop = useCallback(async (stopId, classification, note = '') => {
    try {
      await axios.post(
        `${API}/api/chauffeur/shifts/current/gps-stop/classify`,
        { stop_id: stopId, classification, note },
        { headers: getHeaders() }
      );
      setPendingStop(null);
      // Reset anchor after classification
      if (anchorRef.current) {
        anchorRef.current.since = Date.now();
      }
    } catch (err) {
      console.warn('[StopDetector] Classify failed:', err.message);
    }
  }, []);

  // Start/stop position watching based on shift state
  useEffect(() => {
    if (!shiftActive) {
      // Cleanup
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      anchorRef.current = null;
      setPendingStop(null);
      setDetecting(false);
      return;
    }

    if (!navigator.geolocation) return;
    setDetecting(true);

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        const anchor = anchorRef.current;

        if (!anchor) {
          resetAnchor(lat, lng);
          return;
        }

        const dist = distanceM(anchor.lat, anchor.lng, lat, lng);

        if (dist > MOVEMENT_THRESHOLD_M) {
          // Moved — reset anchor
          resetAnchor(lat, lng);
        } else {
          // Still in same area — check duration
          const elapsed = Date.now() - anchor.since;
          if (elapsed >= STOP_THRESHOLD_MS && !pendingStop) {
            // 5+ minutes immobile → report stop
            reportStop();
          }
        }
      },
      () => { /* GPS error — ignore, will retry */ },
      { enableHighAccuracy: true, timeout: 30000, maximumAge: 10000 }
    );

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [shiftActive, resetAnchor, reportStop, pendingStop]);

  return {
    pendingStop,      // null or { stop_id } — show popup when non-null
    classifyStop,     // (stopId, classification, note) => Promise
    detecting,        // boolean — is GPS monitoring active
    dismissStop: () => setPendingStop(null), // emergency dismiss (shouldn't normally be used)
  };
}
